/**
 * Calendar Statistics Component
 *
 * Displays key appointment statistics (total, scheduled, confirmed and
 * upcoming today) as a row of shared stat cards.
 */

import React from 'react';
import { theme } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CalendarOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { StatCard } from '@/components/ui/StatCard';
import { StatCardGrid } from '@/components/ui/StatCardGrid';
import { type AppointmentStats } from './calendarUtils';

interface CalendarStatsProps {
  /** Statistics data to display */
  stats: AppointmentStats;
}

/**
 * Statistics cards component for calendar overview
 * Displays key metrics in a responsive grid
 */
export const CalendarStats: React.FC<CalendarStatsProps> = ({ stats }) => {
  const { t } = useTranslation('manage');
  const { token } = theme.useToken();

  return (
    <StatCardGrid>
      <StatCard label={t('appointmentComponents.stats.totalAppointments')} value={stats.total} icon={<CalendarOutlined />} />
      <StatCard label={t('appointmentComponents.stats.scheduled')} value={stats.scheduled} icon={<ClockCircleOutlined />} color={token.colorInfo} />
      <StatCard
        label={t('appointmentComponents.stats.confirmed')}
        value={stats.confirmed}
        icon={<CheckCircleOutlined />}
        color={token.colorSuccess}
      />
      <StatCard
        label={t('appointmentComponents.stats.upcomingToday')}
        value={stats.upcomingToday}
        icon={<FieldTimeOutlined />}
        color={token.colorWarning}
      />
    </StatCardGrid>
  );
};
